import React, { useState, useEffect, useRef, useCallback } from 'react';
import TextInputDuo from './TextInputDuo';
import { Button } from './index';
import { useTranslation } from '../context/TranslationContext';

const TextInputDuoList = ({
  title,
  initialCount = 1,
  allowAdd = false,
  numbering = false,
  label1,
  label2,
  placeholder1,
  placeholder2,
  initialData = [],
  onChange,
  radius = 'sm', // sm, md, lg
  compact = false,
  bordered = false,
  className = ''
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState(() => {
    // Initialiser avec les données existantes ou créer des entrées vides
    const initialItems = [];
    const count = Math.max(initialCount, initialData.length);
    
    for (let i = 0; i < count; i++) {
      if (i < initialData.length) {
        initialItems.push(initialData[i]);
      } else {
        initialItems.push({ field1: '', field2: '' });
      }
    }
    
    return initialItems;
  });

  // Utiliser useRef pour éviter les boucles infinies avec onChange
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Flag pour éviter les notifications lors de la mise à jour depuis initialData
  const isUpdatingFromInitialData = useRef(false);

  // Mettre à jour les items quand initialData change (sans notifier le parent)
  useEffect(() => {
    // Créer toujours les nouveaux items basés sur initialData
    const newItems = [];
    const count = Math.max(initialCount, initialData.length);
    
    for (let i = 0; i < count; i++) {
      if (i < initialData.length) {
        newItems.push(initialData[i]);
      } else {
        newItems.push({ field1: '', field2: '' });
      }
    }
    
    // Mettre à jour sans condition pour s'assurer que les changements sont pris en compte
    isUpdatingFromInitialData.current = true;
    setItems(newItems);
    
    // Réinitialiser le flag après un délai court
    setTimeout(() => {
      isUpdatingFromInitialData.current = false;
    }, 0);
  }, [initialData, initialCount]);

  // Notifier le parent quand les données changent (sauf lors de la mise à jour depuis initialData)
  useEffect(() => {
    if (!isUpdatingFromInitialData.current && onChangeRef.current) {
      onChangeRef.current(items);
    }
  }, [items]);

  const handleItemChange = (index, newData) => {
    setItems(prevItems => {
      const updatedItems = [...prevItems];
      updatedItems[index] = newData;
      return updatedItems;
    });
  };

  const handleAddItem = () => {
    setItems(prevItems => [...prevItems, { field1: '', field2: '' }]);
  };

  const containerClasses = [
    'wa-text-input-duo-list',
    `wa-text-input-duo-list--radius-${radius}`,
    compact && 'wa-text-input-duo-list--compact',
    bordered && 'wa-text-input-duo-list--bordered',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      {title && (
        <h3 className="wa-text-input-duo-list__title">{title}</h3>
      )}
      
      <div className="wa-text-input-duo-list__items">
        {items.map((item, index) => (
          <TextInputDuo
            key={index}
            label1={label1}
            label2={label2}
            placeholder1={placeholder1}
            placeholder2={placeholder2}
            value1={item.field1}
            value2={item.field2}
            number={numbering ? index + 1 : undefined}
            radius={radius}
            onChange={(newData) => handleItemChange(index, newData)}
          />
        ))}
      </div>
      
      {allowAdd && (
        <div className="wa-text-input-duo-list__add-button-container">
          <Button
            variant="secondary"
            onClick={handleAddItem}
            className="wa-text-input-duo-list__add-button"
          >
            +
          </Button>
        </div>
      )}
    </div>
  );
};

export default TextInputDuoList;
